import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import useWin98Scope from '../hooks/useWin98Scope';
import Layout from '../components/Layout';
import { Sparkles, Loader2, ArrowLeft, Shield } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const PROJECT_TYPES = ['Full build', 'Armor', '3D prints', 'Props', 'Other'];
const inputCls = 'win-input';

// "Request a custom build or print" form. Creates a draft quote in KyberBusiness
// for The Star Forge to price by hand. Public (no login) for marketing-site
// visitors; signed-in users get the standard app chrome (sidebar + hamburger).
const CommissionPage = () => {
  const { user, loading } = useAuth();
  // Standalone (logged-out) branch renders outside Layout, so it toggles the
  // win98 scope itself. Called unconditionally before any branch/early-return
  // per the rules of hooks; harmless double-toggle when Layout also scopes it.
  useWin98Scope();

  const [form, setForm] = useState({
    client_name: '', client_email: '', project_type: 'Full build', character: '',
    description: '', budget_range: '', target_event: '', reference_urls: '', website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.client_email.trim() || !form.description.trim()) {
      toast.error('Please add your name, email, and a description.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/commission-request', form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const content = done ? (
          <div className="win-window">
            <div className="win-titlebar"><span>Request sent</span></div>
            <div className="p-8 text-center">
              <p className="text-2xl font-bold">✓ Request sent</p>
              <p className="mt-2">Thanks. The Star Forge will review your request and reply with a quote by email.</p>
              <Link to="/"><button type="button" className="win-btn win-btn-default mt-6">Back home</button></Link>
            </div>
          </div>
        ) : (
          <div className="win-window">
            <div className="win-titlebar"><span>Request a commission</span></div>
            <div className="p-6">
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5" /> The Star Forge
                </p>
                <p className="mt-2">Tell us what you want built or printed. We will put together a quote and email it to you. There is no charge to ask.</p>
              </div>

              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Your name *</Label>
                    <Input value={form.client_name} onChange={set('client_name')} className={inputCls} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={form.client_email} onChange={set('client_email')} className={inputCls} required />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project type</Label>
                    <select value={form.project_type} onChange={set('project_type')} className="win-input win-select w-full">
                      {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Character or subject</Label>
                    <Input value={form.character} onChange={set('character')} placeholder="e.g. Mandalorian, Master Chief" className={inputCls} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>What do you want made? *</Label>
                  <Textarea value={form.description} onChange={set('description')} rows={5}
                    placeholder="Describe the build or prints, sizing, materials, finish, anything that helps us quote it."
                    className={inputCls} required />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget range</Label>
                    <Input value={form.budget_range} onChange={set('budget_range')} placeholder="optional" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label>Need it by / event</Label>
                    <Input value={form.target_event} onChange={set('target_event')} placeholder="optional, e.g. DragonCon 2026" className={inputCls} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Reference links</Label>
                  <Textarea value={form.reference_urls} onChange={set('reference_urls')} rows={2}
                    placeholder="Paste links to reference images (Imgur, Drive, socials). Optional." className={inputCls} />
                </div>

                {/* Honeypot: hidden from people, catches bots */}
                <input type="text" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off"
                  className="hidden" aria-hidden="true" />

                <button type="submit" disabled={submitting} className="win-btn win-btn-accent w-full">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send request'}
                </button>
                <p className="text-xs text-center">We never share your details. A quote is not a commitment to buy.</p>
              </form>
            </div>
          </div>
        );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--win-titlebar-start)]" />
      </div>
    );
  }

  // Signed-in users get the standard app chrome (sidebar + hamburger) like every other page.
  if (user) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">{content}</div>
      </Layout>
    );
  }

  // Public visitors (from the marketing site) get a standalone branded page.
  return (
    <div className="min-h-screen bg-slate-950">
      <header>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg">
            <Sparkles className="w-6 h-6 text-[var(--win-titlebar-start)]" />
            KyberCosplay
          </Link>
          <Link to="/" className="text-sm inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">{content}</main>
    </div>
  );
};

export default CommissionPage;
